import { useEffect, useRef, useState } from "react";
import Controls from "../../components/visualizations/Controls.jsx";
import VisualizationGrid from "../../components/visualizations/VisualizationGrid.jsx";
import { getVisualizations } from "../../services/visualizations.service.js";
import LoadTrigger from "../../components/common/LoadTrigger.jsx";
import PageHeader from "../../components/common/PageHeader.jsx";
import VisualizationCard from "../../components/visualizations/VisualizationCard.jsx";

const Visualizations = () => {
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("publishedAt");
  const [sortOrder, setSortOrder] = useState("asc");
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [records, setRecords] = useState([]);
  const pageRef = useRef(1);
  const reqIdRef = useRef(1);
  const syncIsLoadingRef = useRef(false);
  const sortOptions = {
    title: "title",
    PublishedAt: "publishedAt",
    views: "views",
    likes: "likesCount",
  };

  const onLoadMore = async (reset = false) => {
    if (!reset && (syncIsLoadingRef.current || !hasMore)) return;

    setIsLoading(true);
    syncIsLoadingRef.current = true;
    try {
      const query = {
        page: reset ? 1 : pageRef.current,
        sortBy,
        sortOrder,
        ...(search ? { search } : {}),
      };
      if (reset) {
        pageRef.current = 2;
        ++reqIdRef.current;
        setHasMore(true);
      } else {
        pageRef.current++;
      }
      const reqId = reqIdRef.current;

      const visualizationsData = await getVisualizations(query);

      if (reqId !== reqIdRef.current) {
        return;
      }
      setHasMore(visualizationsData.page < visualizationsData.totalPages);
      setRecords((prev) =>
        reset
          ? visualizationsData.records
          : [...prev, ...visualizationsData.records],
      );
    } catch (error) {
      --pageRef.current;
    } finally {
      setIsLoading(false);
      syncIsLoadingRef.current = false;
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(async () => await onLoadMore(true), 500);
    return () => clearTimeout(timeoutId);
  }, [search, sortBy, sortOrder]);

  useEffect(() => {
    // Title
    document.title = "Interactive Visualizations | VisualLab";

    // Meta description
    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement("meta");
      desc.name = "description";
      document.head.appendChild(desc);
    }
    desc.content =
      "Explore interactive visualizations to understand complex concepts visually. Learn algorithms, data structures, and more with VisualLab.";

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}/visualizations`;

    // Structured data
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Interactive Visualizations",
      description:
        "A collection of interactive visual explanations created on VisualLab.",
      url: `${window.location.origin}/visualizations`,
    });

    document.head.appendChild(script);
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    robots.content = "index, follow";

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <>
      <PageHeader
        title={"Visualizations"}
        description={
          "Explore interactive visualizations created by the community."
        }
      />
      <Controls
        setDebouncedSearch={setSearch}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        sortOptions={sortOptions}
      />
      <VisualizationGrid
        records={records}
        isLoading={isLoading}
        Card={VisualizationCard}
      />
      <LoadTrigger
        hasMore={hasMore}
        isLoading={isLoading}
        onLoadMore={onLoadMore}
      />
    </>
  );
};

export default Visualizations;
